import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { uniqArray, sortData } from "src/utils/common";
import { getErrorDetails } from "src/Hooks/useErrorDetails";

/**
 * Custom hook for converting series data
 * Handles the conversion of raw series data into formatted table data with columns
 */
const useSeriesDataConverter = () => {
  const { t } = useTranslation(["flight-simulator", "common"]);

  const columnsSeriesTable = [
    {
      dataField: "param_filename",
      text: "param_filename",
    },
  ];

  /**
   * Processes aerodynamic coefficients based on version
   * - New version: Uses aerodynamic_coefficients field directly
   * - Old version: Uses aerodynamic_coefficients_1 and aerodynamic_coefficients_2 as separate fields
   * @param {Object} seriesItem - The series item containing aerodynamic data
   * @param {boolean} isNewVersion - Flag indicating if it's a new version
   * @returns {Object|null} Aerodynamic coefficients or null
   */
  const processAerodynamicCoefficients = useCallback((seriesItem, isNewVersion) => {
    if (isNewVersion) {
      // For new version, use aerodynamic_coefficients field directly
      const existingAerodynamicCoefficients = seriesItem["aerodynamic_coefficients"];

      if (existingAerodynamicCoefficients) {
        const existingValue =
          Array.isArray(existingAerodynamicCoefficients?.value) && existingAerodynamicCoefficients?.value?.length > 0
            ? [...existingAerodynamicCoefficients.value].join(", ")
            : existingAerodynamicCoefficients?.value || "";

        if (!isEmpty(existingValue)) {
          return {
            value: existingValue,
            unit: existingAerodynamicCoefficients?.unit,
          };
        }
      }
    }

    return null;
  }, []);

  /**
   * Processes series item fields and generates column data
   * @param {Object} seriesItem - The series item to process
   * @returns {Object} Object containing columnsData and columnsName
   */
  const processSeriesItemFields = useCallback((seriesItem) => {
    let columnsData = {};
    let columnsName = [];

    Object.keys(seriesItem).forEach((field) => {
      if (field === "aerodynamic_coefficients") {
        const fieldValue =
          Array.isArray(seriesItem[field]?.value) && seriesItem[field]?.value?.length > 0
            ? [...seriesItem[field]?.value].join(", ")
            : seriesItem[field]?.value;

        if (!isEmpty(fieldValue)) {
          columnsData = {
            ...columnsData,
            [field]: fieldValue,
          };

          columnsName.push({
            dataField: `${field}`,
            text: `${field} ${seriesItem[field]?.unit ? `(${seriesItem[field]?.unit})` : ""}`,
          });
        }
        return;
      }

      columnsData = {
        ...columnsData,
        [field]:
          Array.isArray(seriesItem[field]?.value) && seriesItem[field]?.value?.length > 0
            ? [...seriesItem[field]?.value].join(", ")
            : seriesItem[field]?.value,
      };

      columnsName.push({
        dataField: `${field}`,
        text: `${field} ${seriesItem[field]?.unit ? `(${seriesItem[field]?.unit})` : ""}`,
      });
    });

    return { columnsData, columnsName };
  }, []);

  /**
   * Converts raw series data into formatted table data with columns
   * @param {Array} data - Raw series data array
   * @param {boolean} isNewVersion - Flag indicating if it's a new version
   * @returns {Object} Object containing dataSeries and columnSeries
   */
  const convertDataSeries = useCallback(
    (data, isNewVersion = false) => {
      let allColumnsName = [];

      const dataSeries = data.map((series) => {
        const seriesItem = series?.jsonUpdateContent?.default || series.jsonObject?.default || {};
        const { param_filename, serie_file_name, serie_name, jsonObject } = series;

        const aerodynamicCoefficients = processAerodynamicCoefficients(seriesItem, isNewVersion);
        const { columnsData, columnsName } = processSeriesItemFields(seriesItem);

        // Add aerodynamic coefficients only for new version when not already present
        if (aerodynamicCoefficients && !seriesItem["aerodynamic_coefficients"]) {
          columnsData.aerodynamic_coefficients = aerodynamicCoefficients.value;
          columnsName.push({
            dataField: "aerodynamic_coefficients",
            text: "aerodynamic_coefficients",
          });
        }

        allColumnsName = [...allColumnsName, ...columnsName];

        return {
          ...series,
          paramList: { param_filename, series: serie_name, ...columnsData },
          name: serie_file_name,
          fileContent: jsonObject,
          errorList: series?.errors?.length ? getErrorDetails(series.errors, serie_file_name, t) : [],
        };
      });

      return {
        dataSeries: sortData(sortData(dataSeries, "param_filename"), "serie_name"),
        columnSeries: uniqArray([...columnsSeriesTable, ...uniqArray(allColumnsName)]),
      };
    },
    [processAerodynamicCoefficients, processSeriesItemFields, t]
  );

  return {
    convertDataSeries,
  };
};

export default useSeriesDataConverter;
